import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { useImportMembersMutation } from "@/api/clubsApi";
import { useSidePanelStack } from "@/components/ui/SidePanelStack";
import { SidePanelBody } from "@/components/ui/SidePanel";
import {
  parseImportFile,
  meetupSourceConfig,
  type ParsedImportMember,
  type ParseImportFileResult,
} from "@tinyclub/shared/utils";
import { InvitationDashboard } from "./InvitationDashboard";

interface MeetupImportWizardProps {
  clubId: string;
  onClose: () => void;
}

type WizardStep = "upload" | "review" | "importing" | "done";

export function MeetupImportWizard({ clubId, onClose }: MeetupImportWizardProps) {
  const [step, setStep] = useState<WizardStep>("upload");
  const [parseResult, setParseResult] = useState<ParseImportFileResult | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<Map<number, ParsedImportMember>>(new Map());
  const [editedMembers, setEditedMembers] = useState<Map<number, ParsedImportMember>>(new Map());
  const [importResult, setImportResult] = useState<{
    results: Array<{ name: string; email?: string; status: string; error?: string }>;
    summary: {
      addedDirectly: number;
      alreadyMembers: number;
      invited: number;
      savedWithoutEmail: number;
      errors: number;
      emailsSentImmediately: number;
      emailsQueued: number;
    };
  } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { push } = useSidePanelStack();

  const [importMembers, { isLoading: isImporting }] = useImportMembersMutation();

  const handleFileSelect = useCallback(async (file: File) => {
    setParseError(null);
    try {
      const result = await parseImportFile(file, meetupSourceConfig);

      if (result.warnings.length > 0 && result.members.length === 0) {
        setParseError(result.warnings.join(". "));
        return;
      }

      setParseResult(result);

      // Select all members by default and create editable copies
      const selected = new Map<number, ParsedImportMember>();
      const edited = new Map<number, ParsedImportMember>();
      result.members.forEach((member, index) => {
        selected.set(index, member);
        edited.set(index, { ...member });
      });
      setSelectedMembers(selected);
      setEditedMembers(edited);
      setStep("review");
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Failed to parse file");
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const toggleMember = useCallback((index: number) => {
    setSelectedMembers((prev) => {
      const next = new Map(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        const edited = editedMembers.get(index);
        if (edited) next.set(index, edited);
      }
      return next;
    });
  }, [editedMembers]);

  const toggleAll = useCallback(() => {
    if (!parseResult) return;
    setSelectedMembers((prev) => {
      if (prev.size === parseResult.members.length) {
        return new Map();
      }
      const next = new Map<number, ParsedImportMember>();
      parseResult.members.forEach((_, index) => {
        const edited = editedMembers.get(index);
        if (edited) next.set(index, edited);
      });
      return next;
    });
  }, [parseResult, editedMembers]);

  const updateMemberField = useCallback(
    (index: number, field: keyof ParsedImportMember, value: string) => {
      setEditedMembers((prev) => {
        const next = new Map(prev);
        const member = next.get(index);
        if (member) {
          next.set(index, { ...member, [field]: value });
          // Also update in selected if it's selected
          if (selectedMembers.has(index)) {
            setSelectedMembers((sel) => {
              const nextSel = new Map(sel);
              nextSel.set(index, { ...member, [field]: value });
              return nextSel;
            });
          }
        }
        return next;
      });
    },
    [selectedMembers]
  );

  const handleImport = useCallback(async () => {
    setStep("importing");
    setImportError(null);

    const membersToImport = Array.from(selectedMembers.values()).map((member) => ({
      name: member.name,
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email || undefined,
      role: member.role,
      sourceMetadata: member.sourceMetadata,
    }));

    try {
      const result = await importMembers({
        clubId,
        members: membersToImport,
        source: "meetup_import",
      }).unwrap();

      setImportResult(result);
      setStep("done");
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Import failed. Please try again.");
      setStep("review");
    }
  }, [selectedMembers, clubId, importMembers]);

  const handleViewInvitations = useCallback(() => {
    push({
      id: "invitations",
      title: "Invitations",
      content: <InvitationDashboard clubId={clubId} />,
    });
  }, [push, clubId]);

  return (
    <SidePanelBody className="pt-0">
      {step === "upload" && (
        <UploadStep
          isDragging={isDragging}
          parseError={parseError}
          fileInputRef={fileInputRef}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onFileInputChange={handleFileInputChange}
          onBrowseClick={() => fileInputRef.current?.click()}
        />
      )}

      {step === "review" && parseResult && (
        <ReviewStep
          parseResult={parseResult}
          selectedMembers={selectedMembers}
          editedMembers={editedMembers}
          importError={importError}
          onToggleMember={toggleMember}
          onToggleAll={toggleAll}
          onUpdateField={updateMemberField}
          onBack={() => {
            setStep("upload");
            setParseResult(null);
          }}
          onImport={handleImport}
        />
      )}

      {step === "importing" && (
        <ImportingStep
          total={selectedMembers.size}
        />
      )}

      {step === "done" && importResult && (
        <DoneStep
          summary={importResult.summary}
          onViewInvitations={handleViewInvitations}
          onClose={onClose}
        />
      )}
    </SidePanelBody>
  );
}

// --- Upload Step ---
function UploadStep({
  isDragging,
  parseError,
  fileInputRef,
  onDrop,
  onDragOver,
  onDragLeave,
  onFileInputChange,
  onBrowseClick,
}: {
  isDragging: boolean;
  parseError: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBrowseClick: () => void;
}) {
  return (
    <div className="space-y-6 pb-4">
      <div className="space-y-2">
        <h3 className="text-body-1-medium text-text-primary">Upload Meetup export</h3>
        <p className="text-body-2 text-text-secondary">
          Export your member list from Meetup.com and upload the file here. We support .xls, .xlsx, and .csv formats.
        </p>
      </div>

      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors cursor-pointer ${
          isDragging
            ? "border-text-primary bg-surface-1"
            : "border-text-secondary hover:border-text-primary"
        }`}
        onClick={onBrowseClick}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-surface-1 flex items-center justify-center">
            <Icon name="file-arrow-up" size={24} className="text-text-secondary" />
          </div>
          <div>
            <p className="text-body-1-medium text-text-primary">
              Drop your file here or click to browse
            </p>
            <p className="text-body-3 text-text-tertiary mt-1">
              Supports .xls, .xlsx, .csv
            </p>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        onChange={onFileInputChange}
        className="hidden"
      />

      {parseError && (
        <div className="bg-accent-negative/10 border border-accent-negative/20 rounded-xl p-3">
          <p className="text-body-2 text-accent-negative">{parseError}</p>
        </div>
      )}

      <div className="bg-surface-1 rounded-2xl p-4 space-y-3">
        <h4 className="text-body-2-medium text-text-primary">How to export from Meetup</h4>
        <ol className="text-body-3 text-text-secondary space-y-2 list-decimal list-inside">
          <li>Go to your Meetup group page</li>
          <li>Click "Members" in the navigation</li>
          <li>Click "Export member list" (organizer access required)</li>
          <li>Upload the downloaded .xls file here</li>
        </ol>
      </div>
    </div>
  );
}

// --- Review Step ---
function ReviewStep({
  parseResult,
  selectedMembers,
  editedMembers,
  importError,
  onToggleMember,
  onToggleAll,
  onUpdateField,
  onBack,
  onImport,
}: {
  parseResult: ParseImportFileResult;
  selectedMembers: Map<number, ParsedImportMember>;
  editedMembers: Map<number, ParsedImportMember>;
  importError: string | null;
  onToggleMember: (index: number) => void;
  onToggleAll: () => void;
  onUpdateField: (index: number, field: keyof ParsedImportMember, value: string) => void;
  onBack: () => void;
  onImport: () => void;
}) {
  const allSelected = selectedMembers.size === parseResult.members.length;

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-body-1-medium text-text-primary">Review members</h3>
          <p className="text-body-3 text-text-secondary">
            {selectedMembers.size} of {parseResult.members.length} selected
            {parseResult.skippedRows > 0 && ` (${parseResult.skippedRows} skipped)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            Back
          </Button>
          <Button
            size="sm"
            onClick={onImport}
            disabled={selectedMembers.size === 0}
          >
            Import {selectedMembers.size}
          </Button>
        </div>
      </div>

      {importError && (
        <div className="bg-accent-negative/10 border border-accent-negative/20 rounded-xl p-3">
          <p className="text-body-2 text-accent-negative">{importError}</p>
        </div>
      )}

      {parseResult.warnings.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
          {parseResult.warnings.map((w, i) => (
            <p key={i} className="text-body-3 text-amber-600">{w}</p>
          ))}
        </div>
      )}

      <div className="border border-border-subtle rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 bg-surface-1 border-b border-border-subtle text-body-3-medium text-text-secondary">
          <button
            onClick={onToggleAll}
            className="w-5 h-5 rounded border border-border-tertiary flex items-center justify-center flex-shrink-0"
          >
            {allSelected && <Icon name="check" size={10} className="text-text-primary" />}
          </button>
          <span className="w-[140px] flex-shrink-0">Name</span>
          <span className="w-[100px] flex-shrink-0">First</span>
          <span className="w-[100px] flex-shrink-0">Last</span>
          <span className="flex-1 min-w-[140px]">Email</span>
          <span className="w-[80px] flex-shrink-0">Role</span>
        </div>

        {/* Rows */}
        <div className="max-h-[400px] overflow-y-auto">
          {parseResult.members.map((_, index) => {
            const member = editedMembers.get(index);
            if (!member) return null;
            const isSelected = selectedMembers.has(index);

            return (
              <div
                key={index}
                className={`flex items-center gap-2 px-3 py-2 border-b border-border-subtle last:border-b-0 ${
                  isSelected ? "bg-background" : "bg-surface-1/50 opacity-60"
                }`}
              >
                <button
                  onClick={() => onToggleMember(index)}
                  className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                    isSelected ? "border-accent-primary bg-accent-primary" : "border-border-tertiary"
                  }`}
                >
                  {isSelected && <Icon name="check" size={10} className="text-accent-primary-text" />}
                </button>
                <span className="w-[140px] flex-shrink-0 text-body-3 text-text-primary truncate" title={member.name}>
                  {member.name}
                </span>
                <input
                  className="w-[100px] flex-shrink-0 text-body-3 bg-transparent border border-border-subtle rounded px-1.5 py-0.5 text-text-primary focus:outline-none focus:border-accent-primary"
                  value={member.firstName}
                  onChange={(e) => onUpdateField(index, "firstName", e.target.value)}
                />
                <input
                  className="w-[100px] flex-shrink-0 text-body-3 bg-transparent border border-border-subtle rounded px-1.5 py-0.5 text-text-primary focus:outline-none focus:border-accent-primary"
                  value={member.lastName}
                  onChange={(e) => onUpdateField(index, "lastName", e.target.value)}
                />
                <input
                  className="flex-1 min-w-[140px] text-body-3 bg-transparent border border-border-subtle rounded px-1.5 py-0.5 text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary"
                  value={member.email || ""}
                  placeholder="email@example.com"
                  onChange={(e) => onUpdateField(index, "email", e.target.value)}
                />
                <select
                  className="w-[80px] flex-shrink-0 text-body-3 bg-transparent border border-border-subtle rounded px-1 py-0.5 text-text-primary focus:outline-none focus:border-accent-primary"
                  value={member.role}
                  onChange={(e) => onUpdateField(index, "role", e.target.value)}
                >
                  <option value="member">Member</option>
                  <option value="cohost">Co-host</option>
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// --- Importing Step ---
function ImportingStep({ total }: { total: number }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-accent-primary" />
      <div className="text-center">
        <p className="text-body-1-medium text-text-primary">Importing members...</p>
        <p className="text-body-3 text-text-secondary mt-1">
          Processing {total} member{total !== 1 ? "s" : ""}
        </p>
      </div>
    </div>
  );
}

// --- Done Step ---
function DoneStep({
  summary,
  onViewInvitations,
  onClose,
}: {
  summary: {
    addedDirectly: number;
    alreadyMembers: number;
    invited: number;
    savedWithoutEmail: number;
    errors: number;
    emailsSentImmediately: number;
    emailsQueued: number;
  };
  onViewInvitations: () => void;
  onClose: () => void;
}) {
  return (
    <div className="space-y-6 pb-4">
      <div className="text-center py-4">
        <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-3">
          <Icon name="check" size={28} className="text-green-500" />
        </div>
        <h3 className="text-h5 text-text-primary">Import complete</h3>
      </div>

      <div className="bg-surface rounded-2xl divide-y divide-border-subtle overflow-hidden">
        {summary.addedDirectly > 0 && (
          <SummaryRow
            icon="user-plus"
            label="Added directly (existing users)"
            count={summary.addedDirectly}
            color="text-green-500"
          />
        )}
        {summary.invited > 0 && (
          <SummaryRow
            icon="envelope"
            label="Invitations created"
            count={summary.invited}
            color="text-accent-primary"
          />
        )}
        {summary.savedWithoutEmail > 0 && (
          <SummaryRow
            icon="user"
            label="Saved without email"
            count={summary.savedWithoutEmail}
            color="text-text-secondary"
          />
        )}
        {summary.alreadyMembers > 0 && (
          <SummaryRow
            icon="user-check"
            label="Already members"
            count={summary.alreadyMembers}
            color="text-text-tertiary"
          />
        )}
        {summary.errors > 0 && (
          <SummaryRow
            icon="circle-exclamation"
            label="Errors"
            count={summary.errors}
            color="text-accent-negative"
          />
        )}
      </div>

      {summary.emailsSentImmediately > 0 && (
        <p className="text-body-3 text-text-secondary text-center">
          {summary.emailsSentImmediately} invitation email{summary.emailsSentImmediately !== 1 ? "s" : ""} sent
          {summary.emailsQueued > 0 && `. ${summary.emailsQueued} more will be sent shortly.`}
        </p>
      )}

      {summary.emailsQueued > 0 && summary.emailsSentImmediately === 0 && (
        <p className="text-body-3 text-text-secondary text-center">
          {summary.emailsQueued} invitation email{summary.emailsQueued !== 1 ? "s" : ""} queued. Send them from the Invitations dashboard.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {(summary.invited > 0 || summary.savedWithoutEmail > 0) && (
          <Button onClick={onViewInvitations} className="w-full">
            View invitations
          </Button>
        )}
        <Button variant="ghost" onClick={onClose} className="w-full">
          Done
        </Button>
      </div>
    </div>
  );
}

function SummaryRow({
  icon,
  label,
  count,
  color,
}: {
  icon: string;
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Icon name={icon} size={16} className={color} />
      <span className="flex-1 text-body-2 text-text-primary">{label}</span>
      <span className={`text-body-2-medium ${color}`}>{count}</span>
    </div>
  );
}
